import {
  deleteIgnoreScanReportItem,
  getIgnoreScanReportItem,
  getRefreshIssuesDomainScanning,
  getRefreshIssuesWebServiceScanning,
  getScanHistoryDomains,
  getScanHistoryWebServices,
  getScanReportRunDomain,
  getScanReportRunWebService,
  getScanReportScheduleDomain,
  getScanReportScheduleWebService,
  getScansSummary,
  postIgnoreScanReportItem,
  postScanReportRunDomain,
  postScanReportRunWebService,
  postScanReportSchedule,
} from "@/services/scanning";
import { TYPE_ISSUE_OVER_TIME_OPTIONS, TYPE_SCHEDULE } from "@/utils/constants";
import {
  calculateTotalIssues,
  getComponentUiText,
  mergeScanCounts,
  mergeScanIssuesCounts,
} from "@/utils/utilities";
import get from "lodash/get";
import { showErrorAPI, showSuccessToast } from "@/services/apiRoutes";
import { Scanning } from "@/services/apiRoutes/routes";
import dayjs from "dayjs";

export type ScansHistoryWebServicesType = {
  isLoadingSchedule: boolean;
  id: string;
  webserviceId: string;
  webserviceUrl: string;
  latestScanIndex: number;
  latestScanTime: number;
  scanReportRuns: [
    {
      scanReportRunId: string;
      scanReportWebserviceRunId: string;
      scanReportRunTypeId: string;
      scanReportRunType: string;
      startedAt: string | null;
      endedAt: string | null;
      runStatusId: string;
      runStatus: string;
      total: string;
      critical: string;
      high: string;
      med: string;
      low: string;
    },
  ];
  scanReportScheduleWebServices: [
    {
      userId: string;
      sheduleId: string;
      rrule: string;
      isAchived: boolean;
      inputs: {
        forceRun: boolean;
        webServiceIds: number[];
        forceRefresh: boolean;
      };
      addedByUserId: string;
      dateAdded: string;
      dateEdited: string | null;
      editedByUserId: string | null;
    },
  ];
  scanReportScheduleDomains: [
    {
      userId: string;
      sheduleId: string;
      rrule: string;
      isAchived: boolean;
      inputs: {
        forceRun: boolean;
        domainIds: number[];
        forceRefresh: boolean;
      };
      addedByUserId: string;
      dateAdded: string;
      dateEdited: string | null;
      editedByUserId: string | null;
    },
  ];
};

export type ScansHistoryDomainsType = {
  id: string;
  domainId: string;
  domainName: string;
  latestScanIndex: number;
  latestScanTime: number;
  scanReportRuns: [
    {
      scanReportRunId: string;
      scanReportDomainRunId: string;
      scanReportRunTypeId: string;
      scanReportRunType: string;
      startedAt: string | null;
      endedAt: string | null;
      runStatusId: string;
      runStatus: string;
      total: string;
      critical: string;
      high: string;
      med: string;
      low: string;
    },
  ];
};

const scanningModule = {
  namespaced: true,
  state() {
    return {
      scanCountsDataDomains: {},
      scanCountsDataWebServices: {},
      scanTargetsCountsDataWebServices: {},
      scanTargetsCountsDataDomains: {},
      scansIssuesCountsDomains: {},
      scansIssuesCountsWebService: {},
      scansIssuesHistoriesDomains: {},
      scansIssuesHistoriesWebService: {},
      listScansHistoryWebServices: [] as ScansHistoryWebServicesType[],
      listScansHistoryDomains: [] as ScansHistoryDomainsType[],
      isLoading: false,
      webserviceScanningId: null,
      domainScanningId: null,
      scanReportScheduleWebServices: [],
      scanReportScheduleDomain: [],
      isLoadingSchedule: true,
      scanReportRun: {
        [TYPE_SCHEDULE.webservices]: {},
        [TYPE_SCHEDULE.domains]: {},
      },
      scanReportItemIgnores: [],
      isLoadingWebServices: true,
      isLoadingDomains: true,
    };
  },

  getters: {
    getScanReportRun: (state: any) => state.scanReportRun,
    getScanReportItemIgnores: (state: any) => state.scanReportItemIgnores || [],
    GET_SCAN_CURRENT_SCORE: (state: any) =>
      state.scanCountsDataDomains.scanTypeCounts[0].scansCountCurrent +
      state.scanCountsDataWebServices.scanTypeCounts[0].scansCountCurrent,
    GET_SCAN_TOTAL_SCORE: (state: any) =>
      state.scanCountsDataDomains.scanTypeCounts[0].scansCountTotal +
      state.scanCountsDataWebServices.scanTypeCounts[0].scansCountTotal,
    GET_SCAN_TARGETS_COUNTS_WEB_SERVICE: (state: any) =>
      state.scanTargetsCountsDataWebServices,
    GET_SCAN_TARGETS_COUNTS_DOMAINS: (state: any) =>
      state.scanTargetsCountsDataDomains,

    GET_SCANS_ISSUES_COUNTS: (state: any) => {
      return mergeScanIssuesCounts(
        state.scansIssuesCountsDomains,
        state.scansIssuesCountsWebService,
      );
    },

    GET_SCAN_COUNTS: (state: any) => {
      return mergeScanCounts(
        state.scanCountsDataDomains.scanTypeCounts,
        state.scanCountsDataWebServices.scanTypeCounts,
      );
    },

    GET_IS_LOADING_WEB_SERVICES: (state: any) => state.isLoadingWebServices,
    GET_IS_LOADING_DOMAINS: (state: any) => state.isLoadingDomains,

    GET_ISSUES_OVER_TIME_COUNTS: (state: any) => (filter) => {
      let combinedResult: any = [];

      if (filter === TYPE_ISSUE_OVER_TIME_OPTIONS.Total) {
        combinedResult = state.listScansHistoryWebServices.concat(
          state.listScansHistoryDomains,
        );
      } else if (filter === TYPE_ISSUE_OVER_TIME_OPTIONS.Domains) {
        combinedResult = state.listScansHistoryDomains;
      } else {
        combinedResult = state.listScansHistoryWebServices;
      }
      let finalData = calculateTotalIssues(combinedResult);
      const currentMonth = dayjs().format("M");
      finalData = finalData
        .slice(Number(currentMonth))
        .concat(finalData.slice(0, Number(currentMonth)));

      const total = finalData.map((item) => item.total);
      const month = finalData.map((item) => {
        return dayjs(item.month).format("M");
      });

      return {
        total,
        month,
        combinedResult,
      };
    },
    GET_SCHEDULED_ITEM:
      (state: ScansHistoryWebServicesType) =>
      (data: { type: keyof typeof TYPE_SCHEDULE; id: any }) => {
        if (data.type === TYPE_SCHEDULE.domains) {
          return state.scanReportScheduleDomains?.find((item) =>
            item.inputs.domainIds.includes(+data.id),
          );
        } else {
          return state.scanReportScheduleWebServices.find((item) =>
            item.inputs.webServiceIds.includes(+data.id),
          );
        }
      },
  },

  mutations: {
    SET_SCAN_COUNTS_DOMAINS_DATA: (state, payload) => {
      state.scanCountsDataDomains = payload;
    },

    SET_SCAN_COUNTS_WEB_SERVICES_DATA: (state, payload) => {
      state.scanCountsDataWebServices = payload;
    },

    SET_SCAN_TARGETS_COUNTS_DOMAINS_DATA: (state, payload) => {
      state.scanTargetsCountsDataDomains = payload;
    },

    SET_SCAN_TARGETS_COUNTS_WEB_SERVICES_DATA: (state, payload) => {
      state.scanTargetsCountsDataWebServices = payload;
    },

    SET_SCANS_ISSUES_COUNTS_DOMAIN_DATA: (state, payload) => {
      if (!payload || payload.length === 0) return;

      const issue = {
        total: 0,
        critical: 0,
        high: 0,
        med: 0,
        low: 0,
      };

      for (const service of payload) {
        if (service.scanReportRuns && service.scanReportRuns.length > 0) {
          const latestRunSuccess = service.scanReportRuns
            .filter((item) => item.runStatus === "OK/DONE")
            .reduce(
              (latest, item) =>
                !latest || new Date(item.startedAt) > new Date(latest.startedAt)
                  ? item
                  : latest,
              null,
            );

          if (!latestRunSuccess) continue;

          issue.total += Number(latestRunSuccess.total);
          issue.critical += Number(latestRunSuccess.critical);
          issue.high += Number(latestRunSuccess.high);
          issue.med += Number(latestRunSuccess.med);
          issue.low += Number(latestRunSuccess.low);
        }
      }

      state.scansIssuesCountsDomains = issue;
    },

    SET_SCANS_ISSUES_COUNTS_WEB_SERVICE_DATA: (state, payload) => {
      if (!payload || payload.length === 0) return;

      const issue = {
        total: 0,
        critical: 0,
        high: 0,
        med: 0,
        low: 0,
      };

      for (const service of payload) {
        if (service.scanReportRuns && service.scanReportRuns.length > 0) {
          const latestRunSuccess = service.scanReportRuns
            .filter((item) => item.runStatus === "OK/DONE")
            .reduce(
              (latest, item) =>
                !latest || new Date(item.startedAt) > new Date(latest.startedAt)
                  ? item
                  : latest,
              null,
            );

          if (!latestRunSuccess) continue;

          issue.total += Number(latestRunSuccess.total);
          issue.critical += Number(latestRunSuccess.critical);
          issue.high += Number(latestRunSuccess.high);
          issue.med += Number(latestRunSuccess.med);
          issue.low += Number(latestRunSuccess.low);
        }
      }

      state.scansIssuesCountsWebService = issue;
    },

    SET_SCANS_ISSUES_HISTORIES_DOMAIN_DATA: (state, payload) => {
      state.scansIssuesHistoriesDomains = payload;
    },

    SET_SCANS_ISSUES_HISTORIES_WEB_SERVICE_DATA: (state, payload) => {
      state.scansIssuesHistoriesWebService = payload;
    },
    SET_SCANS_HISTORY_DOMAINS: (state, data) => {
      state.listScansHistoryDomains = data;
    },
    SET_SCANS_HISTORY_WEB_SERVICES: (state, data) => {
      state.listScansHistoryWebServices = data;
    },
    UPDATE_SCAN_ISSUES_WEB_SERVICES: (state, data) => {
      data.forEach((item2) => {
        const item1 = state.listScansHistoryWebServices.find(
          (item) => item.webserviceId === item2.webserviceId,
        );
        if (item1) {
          item1.scanReportRuns = item2.scanReportRuns;
          item1.webserviceUrl = item2.webserviceUrl;
        }
      });
    },
    UPDATE_SCAN_ISSUES_DOMAINS: (state, data) => {
      data.forEach((item2) => {
        const item1 = state.listScansHistoryDomains.find(
          (item) => item.domainId === item2.domainId,
        );
        if (item1) {
          item1.scanReportRuns = item2.scanReportRuns;
          item1.webserviceUrl = item2.webserviceUrl;
        }
      });
    },
    SET_UPDATE_SCANS_HISTORY_DOMAINS: (state, data) => {
      state.listScansHistoryDomains = state.listScansHistoryDomains.map(
        (item) => {
          data.forEach((element) => {
            if (item.domainId == element.domainId) {
              return item.scanReportRuns.push(element);
            }
          });
          return item;
        },
      );
    },

    SET_UPDATE_SCANS_HISTORY_WEB_SERVICES: (state, data) => {
      state.listScansHistoryWebServices = state.listScansHistoryWebServices.map(
        (item) => {
          data.forEach((element) => {
            if (item.webserviceId == element.webserviceId) {
              return item.scanReportRuns.push(element);
            }
          });
          return item;
        },
      );
    },
    SET_DATA_REFRESH_LOADING: (
      state,
      { isLoading, webserviceIds, domainIds },
    ) => {
      state.isLoading = isLoading;
      state.webserviceScanningId = webserviceIds;
      state.domainScanningId = domainIds;
    },
    SET_SCANS_SCHEDULE_WEB_SERVICES: (state, data) => {
      state.scanReportScheduleWebServices = data;
    },
    SET_SCANS_SCHEDULE_DOMAINS: (state, data) => {
      state.scanReportScheduleDomains = data;
    },
    SET_IS_LOADING_SCHEDULE: (state, data) => {
      state.isLoadingSchedule = data;
    },
    SET_IS_LOADING_WEB_SERVICES: (state, data) => {
      state.isLoadingWebServices = data;
    },
    SET_IS_LOADING_DOMAINS: (state, data) => {
      state.isLoadingDomains = data;
    },
  },

  actions: {
    setIsLoadingSchedule: ({ commit }, payload) => {
      commit("SET_IS_LOADING_SCHEDULE", payload);
    },
    fetchScansSummaryDomainsData: async ({ commit }: any, { scanType }) => {
      const {
        data: { data },
      } = await getScansSummary(scanType);
      commit("SET_SCAN_COUNTS_DOMAINS_DATA", data.scanCounts);
      commit("SET_SCAN_TARGETS_COUNTS_DOMAINS_DATA", data.scansTargetsCounts);
      commit(
        "SET_SCANS_ISSUES_HISTORIES_DOMAIN_DATA",
        data.scansIssuesHistories,
      );
    },

    fetchScansSummaryWebServiceData: async ({ commit }: any, { scanType }) => {
      const {
        data: { data },
      } = await getScansSummary(scanType);
      commit("SET_SCAN_COUNTS_WEB_SERVICES_DATA", data.scanCounts);
      commit(
        "SET_SCAN_TARGETS_COUNTS_WEB_SERVICES_DATA",
        data.scansTargetsCounts,
      );
      commit(
        "SET_SCANS_ISSUES_HISTORIES_WEB_SERVICE_DATA",
        data.scansIssuesHistories,
      );
    },

    fetchScanIssuesHistoryWebServices: async ({ commit }: any) => {
      try {
        const res = await getScanHistoryWebServices();
        const listData = get(res, "data.data.webservices", []);

        commit("SET_SCANS_ISSUES_COUNTS_WEB_SERVICE_DATA", listData);
      } catch (error) {
        showErrorAPI(error, Scanning.getScanHistoryWebServices);
      }
    },

    fetchScanHistoryWebServices: async (
      { commit }: any,
      payload?: { webServiceIds: Array<number> },
    ) => {
      try {
        commit("SET_IS_LOADING_WEB_SERVICES", true);
        const res = await getScanHistoryWebServices(payload?.webServiceIds);
        const listData = get(res, "data.data.webservices", []);

        commit("SET_SCANS_HISTORY_WEB_SERVICES", listData);
        commit("SET_IS_LOADING_WEB_SERVICES", false);
        return res;
      } catch (error) {
        showErrorAPI(error, Scanning.getScanHistoryWebServices);
      }
    },

    fetchScanIssuesHistoryDomains: async ({ commit }: any) => {
      const res = await getScanHistoryDomains();
      const listData = get(res, "data.data.domains", []);

      commit("SET_SCANS_ISSUES_COUNTS_DOMAIN_DATA", listData);
    },

    fetchScanHistoryDomains: async ({ commit }: any) => {
      try {
        commit("SET_IS_LOADING_DOMAINS", true);
        const res = await getScanHistoryDomains();
        const listData = get(res, "data.data.domains", []);

        commit("SET_SCANS_HISTORY_DOMAINS", listData);
        commit("SET_IS_LOADING_DOMAINS", false);
        return res;
      } catch (error) {
        showErrorAPI(error, Scanning.getScanHistoryDomains);
      }
    },

    postScanReportRunDomain: async ({ commit }: any, { runId, domainIds }) => {
      try {
        await postScanReportRunDomain(runId);
        setTimeout(async () => {
          const {
            data: {
              data: { domains },
            },
          } = await getRefreshIssuesDomainScanning(domainIds);
          commit("UPDATE_SCAN_ISSUES_DOMAINS", domains);
        }, 1000);
      } catch (error) {
        showErrorAPI(error, Scanning.postScanReportRunDomain);
      }
    },

    postScanReportRunWebservice: async (
      { commit }: any,
      { runId, webserviceIds },
    ) => {
      try {
        await postScanReportRunWebService(runId);
        setTimeout(async () => {
          const {
            data: {
              data: { webservices },
            },
          } = await getRefreshIssuesWebServiceScanning(webserviceIds);
          commit("UPDATE_SCAN_ISSUES_WEB_SERVICES", webservices);
        }, 1000);
      } catch (error) {
        showErrorAPI(error, Scanning.postScanReportRunWebService);
      }
    },

    refreshIssuesWebServiceScanning: async (
      { commit, dispatch }: any,
      { webserviceIds },
    ) => {
      try {
        const {
          data: {
            data: { webservices },
          },
        } = await getRefreshIssuesWebServiceScanning(webserviceIds);

        commit("UPDATE_SCAN_ISSUES_WEB_SERVICES", webservices);
        dispatch("fetchScanIssuesHistoryWebServices");
      } catch (error) {
        showErrorAPI(error, Scanning.getRefreshIssuesWebServiceScanning);
      }
    },

    refreshIssuesDomainScanning: async (
      { commit, dispatch }: any,
      { domainIds },
    ) => {
      try {
        const {
          data: {
            data: { domains },
          },
        } = await getRefreshIssuesDomainScanning(domainIds);

        commit("UPDATE_SCAN_ISSUES_DOMAINS", domains);
        dispatch("fetchScanIssuesHistoryDomains");
      } catch (error) {
        showErrorAPI(error, Scanning.getRefreshIssuesDomainScanning);
      }
    },
    fetchReportScheduleWebServices: async ({ commit }: any) => {
      try {
        const res = await getScanReportScheduleWebService();
        const listData = get(res, "data.data.scheduleItems", []);
        commit("SET_SCANS_SCHEDULE_WEB_SERVICES", listData);
      } catch (error) {
        showErrorAPI(error, Scanning.getScanReportScheduleWebServices);
      }
    },
    fetchReportScheduleDomain: async ({ commit }: any) => {
      try {
        const res = await getScanReportScheduleDomain();
        const listData = get(res, "data.data.scheduleItems", []);
        commit("SET_SCANS_SCHEDULE_DOMAINS", listData);
      } catch (error) {
        showErrorAPI(error, Scanning.getScanReportScheduleDomains);
      }
    },
    actionScheduleScan: async (
      _: any,
      payload: {
        isEdit?: boolean;
        isDelete?: boolean;
        scanTargetType: keyof typeof TYPE_SCHEDULE;
        data: {
          targetId: number;
          rrule: string;
          isArchived?: boolean;
          scheduleId?: number;
        }[];
      },
    ) => {
      const { isEdit, scanTargetType, data, isDelete } = payload;
      try {
        await postScanReportSchedule(scanTargetType, data, isEdit, isDelete);
        showSuccessToast(
          getComponentUiText("app-api-scanning-schedule-scan-success-1"),
        );
        return true;
      } catch (error) {
        showErrorAPI(error, Scanning.postScanReportScheduleDomains);
        return false;
      }
    },
    fetchScanReportRun: async (
      { state }: any,
      payload: {
        runId: string | number;
        scanTargetType?: keyof typeof TYPE_SCHEDULE;
      },
    ): Promise<any[]> => {
      const { runId, scanTargetType = TYPE_SCHEDULE.webservices } = payload;
      try {
        if (scanTargetType === TYPE_SCHEDULE.domains) {
          const res = await getScanReportRunDomain(runId);
          const data = get(res, "data.data", []);
          state.scanReportRun[TYPE_SCHEDULE.domains][runId] = {
            runId,
            data,
          };
          return data;
        }
        const res = await getScanReportRunWebService(runId);
        const data = get(res, "data.data", []);
        state.scanReportRun[TYPE_SCHEDULE.webservices][runId] = {
          runId,
          data,
        };
        return data;
      } catch (error) {
        showErrorAPI(
          error,
          scanTargetType === TYPE_SCHEDULE.domains
            ? Scanning.getScanReportRunDomain
            : Scanning.getScanReportRunWebService,
        );
        return [];
      }
    },
    fetchIgnoreScanReportItem: async (
      { state }: any,
      ignoreItemIds?: string[],
    ): Promise<
      {
        ignoreId: string;
        ignoreScanItemId: string;
        ignoreForWebserviceId: string;
        ignoreForDomainId: string;
        ignoreNotes: [];
      }[]
    > => {
      try {
        const res = await getIgnoreScanReportItem(ignoreItemIds);
        const data = get(res, "data.data", []);
        state.scanReportItemIgnores = data;
        return data;
      } catch (error) {
        showErrorAPI(error, Scanning.postIgnoreScanReportItem);
        return [];
      }
    },
    handleIgnoreScanReportItem: async (
      { state }: any,
      data: any,
    ): Promise<any> => {
      try {
        const res = await postIgnoreScanReportItem(data);
        const output = get(res, "data.data", data);
        state.scanReportItemIgnores.push(output);
        return output;
      } catch (error) {
        showErrorAPI(error, Scanning.postIgnoreScanReportItem);
        return false;
      }
    },
    handleIncludeScanReportItem: async (
      { state }: any,
      payload: { ignoreItemIds: string[]; data: any },
    ): Promise<any> => {
      try {
        const res = await deleteIgnoreScanReportItem(payload);
        const output = get(res, "data.data", []);
        state.scanReportItemIgnores = output;
        return output;
      } catch (error) {
        showErrorAPI(error, Scanning.postIgnoreScanReportItem);
        return false;
      }
    },
  },
};

export default scanningModule;
